import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { useProfileStats } from './ProfileContext';
import {loadStripe} from '@stripe/stripe-js';
import { forceLogout } from '../utils/authUtils';

// Initialize Stripe outside of component to prevent multiple instances
let stripePromise;
const getStripe = () => {
  if (!stripePromise) {
    // Replace with your actual publishable key
    stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_yourkey');
  }
  return stripePromise;
};

// Cookie detection helper
const detectThirdPartyCookiesBlocked = () => {
  // Check if cookies are blocked via stored flag
  if (window.sessionStorage.getItem('third_party_cookies_blocked') === 'true') {
    return true;
  }
  
  try {
    // Try to access cookies
    document.cookie = "stripe_test=1; SameSite=None; Secure";
    const hasCookie = document.cookie.indexOf('stripe_test') !== -1;
    
    // If no cookie was set, assume cookies are blocked
    const cookiesBlocked = !hasCookie;
    
    // Store result to avoid repeated checks
    window.sessionStorage.setItem('third_party_cookies_blocked', cookiesBlocked ? 'true' : 'false');
    
    return cookiesBlocked;
  } catch (e) {
    console.error('Error checking cookies:', e);
    // Assume cookies are blocked if there's an error
    return true;
  }
};

// Create context
const StripeContext = createContext();

export const StripeProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [isStatusChecked, setIsStatusChecked] = useState(false);
  const { API_BASE_URL, fetchUserProfile, clearCache } = useProfileStats();
  const statusFetchedRef = useRef(false);
  const [cookiesBlocked, setCookiesBlocked] = useState(false);

  const handleAuthFailure = async (response, fallbackMessage = 'Your session has expired. Please login again.') => {
    // Any 401/403 means the session is no longer valid for Stripe actions.
    // Clear all app state and force a clean login.
    let detail = '';
    try {
      const errorData = await response.clone().json();
      detail = errorData?.detail || errorData?.message || '';
    } catch (e) {
      // ignore parse errors
    }

    try {
      clearCache?.();
    } catch (e) {
      // ignore
    }

    toast.info(detail || fallbackMessage, { toastId: 'auth-expired' });
    forceLogout({ redirectTo: '/', reason: detail || `http_${response.status}` });
  };

  // Detect if third-party cookies are blocked
  useEffect(() => {
    const checkCookies = () => {
      const blocked = detectThirdPartyCookiesBlocked();
      setCookiesBlocked(blocked);
      // console.log('Third-party cookies blocked:', blocked);
    };
    
    // Check on mount
    checkCookies();
    
    // Recheck when window gains focus
    window.addEventListener('focus', checkCookies);
    
    return () => {
      window.removeEventListener('focus', checkCookies);
    };
  }, []);

  // Fetch subscription status on mount - only once
  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      // Only fetch if not already fetched and user is logged in
      if (!statusFetchedRef.current && localStorage.getItem('user')) {
        try {
          statusFetchedRef.current = true;
          const userData = JSON.parse(localStorage.getItem('user') || '{}');
          const initialStatus = {
            subscription_type: userData.subscription_type || 'Free',
            tokens_left: userData.tokens_left || 0,
            expiry_date: null
          };
          setSubscriptionStatus(initialStatus);
          
          // Then fetch from server to update if needed
          const status = await getSubscriptionStatus();
          if (status) {
            setSubscriptionStatus(status);
          }
          
          // Mark as checked to prevent future calls
          setIsStatusChecked(true);
        } catch (error) {
          console.error("Failed to fetch initial subscription status", error);
        }
      }
    };

    fetchSubscriptionStatus();
  }, []);

  /**
   * Helper to get token from localStorage or cookies
   */
  const getAuthToken = () => {
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        return user.auth_token || null;
      }
    } catch (err) {
      console.error('Error getting auth token:', err);
    }
    return null;
  };

  /**
   * Initiates the Stripe checkout process for a subscription
   * @param {string} planId - The ID of the plan to subscribe to (basic, premium, pro)
   * @param {string} currency - The currency to use for the transaction (default: 'inr')
   */
  const initiateCheckout = async (planId, currency = 'inr') => {
    setLoading(true);
    setError(null);
    
    try {
      // Check if user is logged in
      const userData = localStorage.getItem('user');
      if (!userData) {
        throw new Error('You must be logged in to subscribe');
      }
      
      // Store the current path for redirect after payment
      const returnUrl = window.location.pathname;
      localStorage.setItem('checkout_return_url', returnUrl);
      
      // Determine if we need to use token-based auth
      const thirdPartyCookiesBlocked = cookiesBlocked;
      const token = getAuthToken();
      
      // Prepare request headers
      const headers = {
        'Content-Type': 'application/json',
      };
      
      // Add authorization header if tokens are available
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Create checkout session
      const response = await fetch(
        `${API_BASE_URL}/subscription/create-checkout-session/${planId}`, 
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ 
            currency,
            third_party_cookies_blocked: thirdPartyCookiesBlocked,
            return_url: `${window.location.origin}/subscription`
          }),
          credentials: thirdPartyCookiesBlocked ? 'omit' : 'include', // Don't send cookies if blocked
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create checkout session');
      }
      
      const data = await response.json();
      
      // Decide which method to use based on cookie availability
      if (thirdPartyCookiesBlocked && data.sessionId) {
        // Use the client-side redirect when cookies are blocked
        const stripe = await getStripe();
        const { error } = await stripe.redirectToCheckout({
          sessionId: data.sessionId
        });
        
        if (error) {
          throw new Error(error.message);
        }
      } else if (data.checkout_url) {
        // Use the server-side redirect URL when cookies work
        window.location.href = data.checkout_url;
      } else {
        throw new Error('Invalid response from server');
      }
      
    } catch (err) {
      const errorMessage = err.message || 'Failed to create checkout session';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Checkout error:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Initiates the Stripe checkout process for a one-time payment
   * @param {string} planId - The ID of the plan to purchase (basic, premium, pro)
   * @param {string} currency - The currency to use for the transaction (default: 'inr')
   */
  const initiateOnetimeCheckout = async (planId, currency = 'inr') => {
    setLoading(true);
    setError(null);
    
    try {
      // Check if user is logged in
      const userData = localStorage.getItem('user');
      if (!userData) {
        throw new Error('You must be logged in to purchase a plan');
      }
      
      // Store the current path for redirect after payment
      const returnUrl = window.location.pathname;
      localStorage.setItem('checkout_return_url', returnUrl);
      
      // Determine if we need to use token-based auth
      const thirdPartyCookiesBlocked = cookiesBlocked;
      const token = getAuthToken();
      
      // Prepare request headers
      const headers = {
        'Content-Type': 'application/json',
      };
      
      // Add authorization header if tokens are available
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Create checkout session
      const response = await fetch(
        `${API_BASE_URL}/subscription/create-onetime-checkout/${planId}`, 
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ 
            currency,
            third_party_cookies_blocked: thirdPartyCookiesBlocked,
            return_url: `${window.location.origin}/subscription`
          }),
          credentials: thirdPartyCookiesBlocked ? 'omit' : 'include', // Don't send cookies if blocked
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create one-time checkout session');
      }
      
      const data = await response.json();
      
      // Decide which method to use based on cookie availability
      if (thirdPartyCookiesBlocked && data.sessionId) {
        // Use the client-side redirect when cookies are blocked
        const stripe = await getStripe();
        const { error } = await stripe.redirectToCheckout({
          sessionId: data.sessionId
        });
        
        if (error) {
          throw new Error(error.message);
        }
      } else if (data.checkout_url) {
        // Use the server-side redirect URL when cookies work
        window.location.href = data.checkout_url;
      } else {
        throw new Error('Invalid response from server');
      }
      
    } catch (err) {
      const errorMessage = err.message || 'Failed to create checkout session';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Checkout error:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Gets the current subscription status of the user
   * @returns {Object} Subscription details including type, tokens left, and expiry date
   */
  const getSubscriptionStatus = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Check if user is logged in
      const userData = localStorage.getItem('user');
      if (!userData) {
        throw new Error('You must be logged in to view subscription status');
      }
      
      // Determine if we need to use token-based auth
      const thirdPartyCookiesBlocked = cookiesBlocked;
      const token = getAuthToken();
      
      // Prepare request headers
      const headers = {
        'Content-Type': 'application/json',
      };
      
      // Add authorization header if tokens are available
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(
        `${API_BASE_URL}/subscription/subscription-status`,
        {
          method: 'GET',
          headers,
          credentials: thirdPartyCookiesBlocked ? 'omit' : 'include', // Don't send cookies if blocked
        }
      );

      if (response.status === 401 || response.status === 403) {
        await handleAuthFailure(response, 'Not authenticated. Please login again.');
        return null;
      }
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to get subscription status');
      }
      
      const data = await response.json();
      
      if (!data) {
        throw new Error('Invalid response from server');
      }
      
      const subscriptionData = {
        subscription_type: data.subscription_type || 'Free',
        tokens_left: data.tokens_left || 0,
        expiry_date: data.expiry_date || null
      };
      
      setSubscriptionStatus(subscriptionData);
      return subscriptionData;
      
    } catch (err) {
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Cancels the user's current subscription
   * @returns {Object} Result of the cancellation request
   */
  const cancelSubscription = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Check if user is logged in
      const userData = localStorage.getItem('user');
      if (!userData) {
        throw new Error('You must be logged in to cancel subscription');
      }
      
      // Determine if we need to use token-based auth
      const thirdPartyCookiesBlocked = cookiesBlocked;
      const token = getAuthToken();
      
      // Prepare request headers
      const headers = {
        'Content-Type': 'application/json',
      };
      
      // Add authorization header if tokens are available
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(
        `${API_BASE_URL}/subscription/cancel-subscription`,
        {
          method: 'POST',
          headers,
          credentials: thirdPartyCookiesBlocked ? 'omit' : 'include', // Don't send cookies if blocked
        }
      );

      if (response.status === 401 || response.status === 403) {
        await handleAuthFailure(response, 'Not authenticated. Please login again.');
        return null;
      }
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to cancel subscription');
      }
      
      const data = await response.json();
      
      toast.success('Subscription will be canceled at the end of the billing period');
      
      // Refresh user profile to get updated subscription status
      await fetchUserProfile(true);
      await getSubscriptionStatus();
      
      return data;
      
    } catch (err) {
      const errorMessage = err.message || 'Failed to cancel subscription';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Cancel subscription error:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Updates the user object in localStorage with subscription information
   * @param {string} subscriptionType - The type of subscription (Basic, Premium, Pro)
   */
  const updateUserSubscriptionInfo = async (subscriptionType, expiryDate, tokensLeft) => {
    try {
      // Default to Free if no subscription type provided
      const subType = subscriptionType || 'Free';
      
      // Get current user data
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      // Update subscription type
      userData.subscription_type = subType;
      userData.expiry_date = expiryDate;
      userData.tokens_left = tokensLeft;
      
      // Save updated user data
      localStorage.setItem('user', JSON.stringify(userData));
      
      // Also refresh from server to ensure all data is current
      if (typeof fetchUserProfile === 'function') {
        await fetchUserProfile(true);
      }
    } catch (error) {
      console.error('Error updating user subscription info:', error);
    }
  };

  /**
   * Handles successful payment completion
   * @param {string} sessionId - The Stripe checkout session ID
   */
  const handlePaymentSuccess = async (sessionId) => {
    setLoading(true);
    setError(null);
    
    try {
      if (!sessionId) {
        throw new Error('No session ID provided');
      }
      
      // Verify the session with the backend
      const status = await verifySession(sessionId);
      
      if (status && status.subscription_type) {
        // Update user info in localStorage
        await updateUserSubscriptionInfo(status.subscription_type, status.expiry_date, status.tokens_left);
        
        // If we got a new auth token from the server, update it
        if (status.auth_token) {
          const userData = JSON.parse(localStorage.getItem('user') || '{}');
          userData.auth_token = status.auth_token;
          localStorage.setItem('user', JSON.stringify(userData));
        }
        
        toast.success(`Successfully subscribed to ${status.subscription_type} plan!`);
      } else {
        throw new Error('Failed to retrieve subscription status');
      }
    } catch (error) {
      const errorMessage = error.message || 'Error handling payment success';
      setError(errorMessage);
      console.error('Error handling payment success:', error);
      toast.error('There was an issue updating your subscription. Please contact support.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Updates the user subscription on the server
   */
  const updateUserSubscription = async (subscriptionType) => {
    setLoading(true);
    setError(null);

    try {
      // Check if user is logged in
      const userData = localStorage.getItem('user');
      if (!userData) {
        throw new Error('You must be logged in to update subscription');
      }

      // Determine if we need to use token-based auth
      const thirdPartyCookiesBlocked = cookiesBlocked;
      const token = getAuthToken();
      
      // Prepare request headers
      const headers = {
        'Content-Type': 'application/json',
      };
      
      // Add authorization header if tokens are available
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(
        `${API_BASE_URL}/subscription/update-subscription`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ subscription_type: subscriptionType }),
          credentials: thirdPartyCookiesBlocked ? 'omit' : 'include', // Don't send cookies if blocked
        }
      );

      if (response.status === 401 || response.status === 403) {
        await handleAuthFailure(response, 'Not authenticated. Please login again.');
        return null;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update subscription');
      }
      
      const data = await response.json();

      // Update local state and localStorage
      const parsedUserData = JSON.parse(userData);
      parsedUserData.subscription_type = subscriptionType;
      parsedUserData.tokens_left = data.tokens_left || 0;
      localStorage.setItem('user', JSON.stringify(parsedUserData));
      
      // Update subscription status
      setSubscriptionStatus({
        subscription_type: subscriptionType,
        tokens_left: data.tokens_left || 0,
        expiry_date: data.expiry_date || null
      });

      toast.success('Subscription updated successfully');
      return data;
      
    } catch (err) {
      const errorMessage = err.message || 'Failed to update subscription';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Update subscription error:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Verifies a checkout session and updates the user's subscription
   * @param {string} sessionId - The Stripe checkout session ID
   */
  const verifySession = async (sessionId) => {
    setLoading(true);
    setError(null);
    
    try {
      // Check if user is logged in
      const userData = localStorage.getItem('user');
      if (!userData) {
        throw new Error('You must be logged in to verify a session');
      }
      
      // Determine if we need to use token-based auth
      const thirdPartyCookiesBlocked = cookiesBlocked;
      const token = getAuthToken();
      
      // Get token from URL if available (for redirect from payment)
      const urlParams = new URLSearchParams(window.location.search);
      const urlToken = urlParams.get('token');
      
      // Prepare request headers
      const headers = {
        'Content-Type': 'application/json',
      };
      
      // Add authorization header if tokens are available
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      } else if (urlToken) {
        headers['Authorization'] = `Bearer ${urlToken}`;
      }

      let url = `${API_BASE_URL}/subscription/verify-session/${sessionId}`;
      
      // Add token parameter if we have one and cookies are blocked
      if ((token || urlToken) && thirdPartyCookiesBlocked) {
        url += `?token=${urlToken || token}`;
      }
      
      const response = await fetch(
        url,
        {
          method: 'GET',
          headers,
          credentials: thirdPartyCookiesBlocked ? 'omit' : 'include', // Don't send cookies if blocked
        }
      );

      if (response.status === 401 || response.status === 403) {
        await handleAuthFailure(response, 'Not authenticated. Please login again.');
        return null;
      }
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to verify session');
      }
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to verify session');
      }
      
      // Update subscription status
      const subscriptionData = {
        subscription_type: data.subscription_type || 'Free',
        tokens_left: data.tokens_left || 0,
        expiry_date: data.expiry_date || null,
        auth_token: data.auth_token
      };
      
      setSubscriptionStatus(subscriptionData);
      
      // Update user info in localStorage
      const parsedUserData = JSON.parse(userData);
      parsedUserData.subscription_type = subscriptionData.subscription_type;
      parsedUserData.tokens_left = subscriptionData.tokens_left;
      
      // Update auth token if provided
      if (data.auth_token) {
        parsedUserData.auth_token = data.auth_token;
      }
      
      localStorage.setItem('user', JSON.stringify(parsedUserData));
      
      return subscriptionData;
      
    } catch (err) {
      const errorMessage = err.message || 'Failed to verify session';
      setError(errorMessage);
      console.error('Session verification error:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Gets the payment history for the current user
   * @returns {Array} Array of payment history objects
   */
  const getPaymentHistory = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Check if user is logged in
      const userData = localStorage.getItem('user');
      if (!userData) {
        throw new Error('You must be logged in to view payment history');
      }
      
      // Determine if we need to use token-based auth
      const thirdPartyCookiesBlocked = cookiesBlocked;
      const token = getAuthToken();
      
      // Prepare request headers
      const headers = {
        'Content-Type': 'application/json',
      };
      
      // Add authorization header if tokens are available
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(
        `${API_BASE_URL}/subscription/payment-history`,
        {
          method: 'GET',
          headers,
          credentials: thirdPartyCookiesBlocked ? 'omit' : 'include', // Don't send cookies if blocked
        }
      );

      if (response.status === 401 || response.status === 403) {
        await handleAuthFailure(response, 'Not authenticated. Please login again.');
        return [];
      }
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to get payment history');
      }
      
      const data = await response.json();
      return data;
      
    } catch (err) {
      const errorMessage = err.message || 'Failed to get payment history';
      setError(errorMessage);
      console.error('Payment history error:', err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Context value
  const value = {
    loading,
    error,
    subscriptionStatus,
    cookiesBlocked,
    initiateCheckout,
    initiateOnetimeCheckout,
    getSubscriptionStatus,
    cancelSubscription,
    handlePaymentSuccess,
    updateUserSubscriptionInfo,
    updateUserSubscription,
    verifySession,
    getPaymentHistory
  };

  return (
    <StripeContext.Provider value={value}>
      {children}
    </StripeContext.Provider>
  );
};

// Custom hook to use the Stripe context
export const useStripe = () => {
  const context = useContext(StripeContext);
  if (context === undefined) {
    throw new Error('useStripe must be used within a StripeProvider');
  }
  return context;
}; 